import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Box, Container, Typography, Paper, Grid, TextField, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Stack
} from '@mui/material';
import {
    Save as SaveIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    DirectionsBus as DirectionsBusIcon
} from '@mui/icons-material';
import styled, { keyframes } from 'styled-components';
import { useToast } from '../../../context/ToastContext';
import axios from 'axios';

const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const TransportSetup = () => {
    const { currentUser } = useSelector((state) => state.user);
    const { showToast } = useToast();

    const [stations, setStations] = useState([]);
    const [station, setStation] = useState('');
    const [price, setPrice] = useState('');
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState('');

    useEffect(() => {
        if (currentUser) {
            fetchStations();
        }
    }, [currentUser]);

    const fetchStations = async () => {
        setFetching(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/TransportSetups/${currentUser._id}`);
            if (response.data && Array.isArray(response.data)) {
                setStations(response.data);
            } else {
                setStations([]);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setFetching(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!station || !price) {
            showToast("Please fill all fields", "warning");
            return;
        }

        setLoading(true);
        try {
            if (editMode) {
                await axios.put(`${process.env.REACT_APP_BASE_URL}/TransportSetup/${editId}`, {
                    station,
                    price: parseFloat(price)
                });
                showToast("Station updated successfully", "success");
            } else {
                await axios.post(`${process.env.REACT_APP_BASE_URL}/TransportSetup`, {
                    station,
                    price: parseFloat(price),
                    school: currentUser._id
                });
                showToast("Station added successfully", "success");
            }
            setStation('');
            setPrice('');
            setEditMode(false);
            setEditId('');
            fetchStations();
        } catch (error) {
            showToast("Error saving station", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (item) => {
        setStation(item.station);
        setPrice(item.price.toString());
        setEditMode(true);
        setEditId(item._id);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this station?")) {
            try {
                await axios.delete(`${process.env.REACT_APP_BASE_URL}/TransportSetup/${id}`);
                showToast("Station deleted successfully", "success");
                fetchStations();
            } catch (error) {
                showToast("Error deleting station", "error");
            }
        }
    };

    return (
        <PageContainer>
            <HeaderWrapper elevation={0}>
                <Container maxWidth="xl">
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 4 }}>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: 'white', mb: 1 }}>
                                Transport Setup
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                Configure transport stations and monthly prices
                            </Typography>
                        </Box>
                    </Stack>
                </Container>
            </HeaderWrapper>

            <Container maxWidth="xl" sx={{ mt: -4, mb: 4 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <StyledPaper elevation={3}>
                            <SectionHeader>
                                <DirectionsBusIcon />
                                <Typography variant="h6" fontWeight="700">
                                    {editMode ? 'Edit Station' : 'Add New Station'}
                                </Typography>
                            </SectionHeader>
                            <Box sx={{ p: 3 }} component="form" onSubmit={handleSave}>
                                <TextField
                                    fullWidth
                                    label="Station Name"
                                    value={station}
                                    onChange={(e) => setStation(e.target.value)}
                                    margin="normal"
                                    required
                                    autoFocus
                                />
                                <TextField
                                    fullWidth
                                    label="Monthly Price (Rs.)"
                                    type="number"
                                    value={price}
                                    onChange={(e) => setPrice(e.target.value)}
                                    margin="normal"
                                    required
                                />
                                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                                    {editMode && (
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            onClick={() => {
                                                setEditMode(false);
                                                setStation('');
                                                setPrice('');
                                            }}
                                            sx={{ borderRadius: '12px' }}
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        fullWidth
                                        disabled={loading}
                                        startIcon={<SaveIcon />}
                                        sx={{ 
                                            borderRadius: '12px',
                                            bgcolor: '#7f56da',
                                            '&:hover': { bgcolor: '#6b44bc' }
                                        }}
                                    >
                                        {loading ? 'Saving...' : (editMode ? 'Update' : 'Add Station')}
                                    </Button>
                                </Box>
                            </Box>
                        </StyledPaper>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <StyledPaper elevation={3}>
                            <SectionHeader>
                                <DirectionsBusIcon />
                                <Typography variant="h6" fontWeight="700">Stations List</Typography>
                            </SectionHeader>
                            <TableContainer sx={{ p: 2 }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Station Name</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Monthly Price (Rs.)</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {fetching ? (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">Loading...</TableCell>
                                            </TableRow>
                                        ) : stations.length > 0 ? (
                                            stations.map((row) => (
                                                <TableRow key={row._id} hover>
                                                    <TableCell>{row.station}</TableCell>
                                                    <TableCell>{row.price}</TableCell>
                                                    <TableCell align="right">
                                                        <IconButton color="primary" onClick={() => handleEdit(row)}>
                                                            <EditIcon />
                                                        </IconButton>
                                                        <IconButton color="error" onClick={() => handleDelete(row._id)}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">No stations configured.</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </StyledPaper>
                    </Grid>
                </Grid>
            </Container>
        </PageContainer>
    );
};

export default TransportSetup;

const PageContainer = styled.div`background: #f8fafc; min-height: 100vh; animation: ${fadeIn} 0.5s ease-out;`;
const HeaderWrapper = styled(Paper)`
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    border-radius: 0 0 40px 40px !important;
    padding-bottom: 60px;
`;
const StyledPaper = styled(Paper)`
    border-radius: 20px !important;
    overflow: hidden;
    height: 100%;
    border: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
`;
const SectionHeader = styled(Box)`
    background: #f8fafc;
    padding: 16px 24px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    gap: 12px;
    color: #1e293b;
    svg { color: #7f56da; }
`;
